import * as Fingerprint from '@fingerprint/agent';
import { isObjectEmpty } from '@lili/utils/helpers';
import { applyColorScheme, getColorSchemeByPartnerId } from '@lili/lili-frontend-common/tokens';
import { initTrackingService } from '@Services/analytics/tracking.service.ts';
import { initLogRocket } from '@Services/analytics/logrocket.service.ts';
import { useBoundStore } from '@Store/index.ts';
import { getCookiesList, getQueryParamsObject } from '@Services/utils/utils.ts';
import { OnboardingFormFields, QueryParamsToStore } from '@Types/types.ts';
import { cookiePrefixesToPickUp, SUBMIT_BUTTON_ID } from '@Types/consts.ts';

import {
  ObservabilityErrorSeverity,
  ObservabilityInit,
  reportError,
} from '@Services/observability/initObservability.ts';
import { flowIdentifierService } from '@Services/init/flow-identifier-service/flowIdentifierService.ts';
import { fetchEnrichedCustomerData } from '@Services/init/partnerWelcome.api.ts';
import { updateCustomerDataFromServer } from '@Services/init/util.ts';
import { processFieldValidationErrors } from '@Services/errors/processFieldValidationErrors.ts';
import { router } from '@Routes/__root';
import { routesModel } from '@Router/router.consts.ts';
import { RoutesMap } from '@Router/router.types.ts';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types.ts';
import { EnvironmentVariables, getEnvironmentVariable } from '../env/environment.service.ts';
import type { RegionCode } from 'google-libphonenumber';

declare global {
  interface Window {
    fpId: string;
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}
const _storeQueryParams = (): void => {
  const setQueryParamValue = useBoundStore.getState().onboardingSlice.setQueryParamValue;
  const setOnboardingIdentifier = useBoundStore.getState().onboardingSlice.setOnboardingIdentifier;
  const qParamsObject = getQueryParamsObject();
  const webUrl = window.location.href;
  const pathName = window.location.pathname;

  // this ifs should be delted when mobile upodated to the new url flow.
  if (webUrl.includes('/multi/intro') && qParamsObject['id']) {
    setQueryParamValue('multiBusinessId', qParamsObject['id']);
    setQueryParamValue('partnerToken', qParamsObject[QueryParamsToStore.TOKEN]);

    return;
  }
  if (webUrl.includes('/ubo/intro') && qParamsObject['customerId']) {
    setQueryParamValue('uboId', qParamsObject['customerId']);
    return;
  }
  // multi business flow
  if (pathName === '/continue-onboarding') {
    const emailParam = qParamsObject[QueryParamsToStore.EMAIL]
      ? { email: qParamsObject[QueryParamsToStore.EMAIL] }
      : { email: undefined };
    router.navigate({ to: routesModel[RoutesMap.LOGIN].path, search: emailParam });
    setOnboardingIdentifier(OnboardingFlowIdentifier.DIRECT_MULTI_BUSINESS);
    setQueryParamValue('emailParam', qParamsObject[QueryParamsToStore.EMAIL]);
    return;
  }
  if (isObjectEmpty(qParamsObject)) return;
  if (qParamsObject[QueryParamsToStore.EMAIL]) {
    setQueryParamValue('emailParam', qParamsObject[QueryParamsToStore.EMAIL]);
  }
  if (qParamsObject[QueryParamsToStore.PARTNER]) {
    setQueryParamValue('discountPartnerId', qParamsObject[QueryParamsToStore.PARTNER]);
  }
  if (qParamsObject[QueryParamsToStore.TOKEN]) {
    setQueryParamValue('partnerToken', qParamsObject[QueryParamsToStore.TOKEN]);
  }
  if (qParamsObject[QueryParamsToStore.REFERRAL]) {
    setQueryParamValue('referralCode', qParamsObject[QueryParamsToStore.REFERRAL]);
  }
  if (qParamsObject[QueryParamsToStore.CUSTOMER]) {
    setQueryParamValue('customerKey', qParamsObject[QueryParamsToStore.CUSTOMER]);
  }
  if (qParamsObject[QueryParamsToStore.SOURCE]) {
    setQueryParamValue('sourcePlatform', qParamsObject[QueryParamsToStore.SOURCE]);
  }
  if (qParamsObject[QueryParamsToStore.LOGIN_TOKEN]) {
    setQueryParamValue('loginToken', qParamsObject[QueryParamsToStore.LOGIN_TOKEN]);
  }
  if (qParamsObject[QueryParamsToStore.CONTINUE_MULTI_BUSINESS]) {
    setQueryParamValue(
      'continueMultiBusiness',
      qParamsObject[QueryParamsToStore.CONTINUE_MULTI_BUSINESS]
    );
  }
  if (
    qParamsObject[QueryParamsToStore.CUSTOMER_ID] ||
    qParamsObject[QueryParamsToStore.CUSTOMER_ID_V2]
  ) {
    setQueryParamValue(
      'customerId',
      qParamsObject[QueryParamsToStore.CUSTOMER_ID] ||
        qParamsObject[QueryParamsToStore.CUSTOMER_ID_V2]
    );
  }
  if (qParamsObject[QueryParamsToStore.AUTH_ID]) {
    setQueryParamValue('authorizedUserId', qParamsObject[QueryParamsToStore.AUTH_ID]);
  }
  if (qParamsObject[QueryParamsToStore.UBO_ID]) {
    setQueryParamValue('uboId', qParamsObject[QueryParamsToStore.UBO_ID]);
  }
  if (qParamsObject[QueryParamsToStore.MULTI_BUSINESS_ID]) {
    setQueryParamValue('multiBusinessId', qParamsObject[QueryParamsToStore.MULTI_BUSINESS_ID]);
  }
};

const _generateCookieObject = (cookieList: string[]) => {
  const cookieObject: Record<string, string> = {};
  cookieList.forEach((cookie: string) => {
    if (
      cookiePrefixesToPickUp.some((prefix: string) => {
        return cookie.toLowerCase().startsWith(prefix.toLowerCase());
      })
    ) {
      let cName = cookie.split('=')[0];
      const cValue = cookie.split('=')[1];

      if (cName === '_ga') {
        cName = 'CID';
      }

      if (cName && cValue) {
        cookieObject[cName] = cValue;
      }
    }
  });

  return cookieObject;
};

const _buildAdSource = (): void => {
  const setQueryParamValue = useBoundStore.getState().onboardingSlice.setQueryParamValue;
  /** Get cookie list from document */
  const cookieList = getCookiesList();
  /** Convert and filter needed cookies to an Object */
  const readyCookieObject = _generateCookieObject(cookieList);
  /** Get query params */
  const qParams = getQueryParamsObject();
  /** Combine qParams object with Cookie adSource while taking priority from qParams */
  let adSource: Record<string, string> = {};
  adSource = { ...readyCookieObject, ...qParams };

  /** If adSource is empty (no cookie & no query params) */
  if (isObjectEmpty(adSource)) {
    try {
      /** Check if there is prev adSource stored in localStorage */
      const adSourceFromStorage = window.localStorage.getItem('adSource');
      setQueryParamValue('adSource', JSON.stringify(adSourceFromStorage));
    } catch (e) {
      console.error(e);
    }
  } else {
    /** Delete invalid parameter keys */
    for (const key in adSource) {
      if (adSource[key].includes('{{') || adSource[key].includes('token')) {
        delete adSource[key];
      }
    }

    /** Declare "af_adid" as afuserid */
    if (adSource['afuserid']) {
      adSource['af_adid'] = adSource['afuserid'];
    }

    /** Save adSource to local storage */
    window.localStorage.setItem('adSource', JSON.stringify(adSource));

    /** Save adSource to local storage */
    setQueryParamValue('adSource', JSON.stringify(adSource));
  }
};

const _injectFingerPrint = async (): Promise<void> => {
  try {
    const fp = Fingerprint.start({
      apiKey: getEnvironmentVariable(EnvironmentVariables.VITE_FINGER_PRINT) || '',
      endpoints: ['https://fp.lili.co'],
    });
    const fpData = await fp.get();
    const { visitor_id } = fpData;
    window.fpId = visitor_id || '';
  } catch (e) {
    console.error(e);
    return;
  }
};

const _listenForInputFocus = (): void => {
  const { setFormFieldError } = useBoundStore.getState().errorSlice;

  window.addEventListener('focusin', (e): void => {
    const shouldScroll = false;
    const idsToIgnoreScroll = [OnboardingFormFields.EMAIL, OnboardingFormFields.PASSWORD];
    /** Will remove the inline error on a filed that returned from the server */
    const elementId = (e.target as HTMLElement)?.id;
    if (!elementId) return;
    if (
      shouldScroll &&
      e?.target &&
      e.target instanceof HTMLInputElement &&
      !idsToIgnoreScroll.includes(elementId as OnboardingFormFields)
    ) {
      e.target.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' });
    }
    setFormFieldError(elementId as keyof typeof OnboardingFormFields, '');
  });
};

const _initFormSubmitOnEnter = (): void => {
  window.addEventListener('keyup', (e) => {
    if (e.key === 'Enter') {
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName === 'INPUT') {
        (activeElement as HTMLElement)?.blur();
      }
      setTimeout(() => {
        const submitButton = document.getElementById(SUBMIT_BUTTON_ID);
        if (submitButton) {
          submitButton.click();
        }
      }, 50);
    }
  });
};

const _initKeyBoardWhitespaceScroll = (): void => {
  document.addEventListener('focusout', () => {
    if (window.innerWidth <= 768) {
      const mobileNavigationHeader = document.getElementById('mobile-navigation-header');
      if (mobileNavigationHeader) {
        mobileNavigationHeader.scrollIntoView({
          block: 'start',
          inline: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  });
};

export const generateCaptchaToken = () =>
  new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(getEnvironmentVariable(EnvironmentVariables.VITE_GRECAPTCHA), { action: 'login' })
        .catch(() => {
          reject(new Error('Captcha Token Failed'));
          const errorToReport = {
            metadata: {
              name: 'Captcha failed',
              message: 'Captcha failed',
            },
            severity: ObservabilityErrorSeverity.ERROR,
            groupingHash: 'captcha-failed',
          };
          reportError(errorToReport);
        })
        .then((token) => {
          window.sessionStorage.setItem('captchaToken', token as string);
          resolve('');
        });
    });
  });

export const initSyncProcess = () => {
  _storeQueryParams();
  _buildAdSource();
  _injectFingerPrint();
  initTrackingService();
  initLogRocket();
  _listenForInputFocus();
  _initFormSubmitOnEnter();
  _initKeyBoardWhitespaceScroll();
  ObservabilityInit();
};

export const initAsyncProcess = async (): Promise<{ redirected?: boolean }> => {
  const onboardingData = useBoundStore.getState().onboardingSlice.getOnboardingData();
  const isAccessToken = useBoundStore.getState().customer.accessToken;

  const redirected = await flowIdentifierService({
    partnerToken: onboardingData.partnerToken,
    referralCode: onboardingData.referralCode,
    customerKey: onboardingData.customerKey,
    sourcePlatform: onboardingData.sourcePlatform,
    customerId: onboardingData.customerId,
    authorizedUserId: onboardingData.authorizedUserId,
    uboId: onboardingData.uboId,
    multiBusinessId: onboardingData.multiBusinessId,
    loginToken: onboardingData.loginToken || '',
    continueMultiBusiness: onboardingData.continueMultiBusiness || false,
  });

  if (redirected) return { redirected: true };

  const customerData = useBoundStore.getState().getCustomerData();

  if (customerData.partnerId) {
    applyColorScheme(getColorSchemeByPartnerId(customerData.partnerId));
  }

  if (customerData.customerId && isAccessToken) {
    console.log('fetching enriched customer data');
    const enrichedCustomerData = (await fetchEnrichedCustomerData()) || {};
    if (enrichedCustomerData?.phoneCountryCode && enrichedCustomerData?.customerData) {
      enrichedCustomerData.customerData.phoneCountryCode =
        enrichedCustomerData.phoneCountryCode as RegionCode;
    }
    updateCustomerDataFromServer(enrichedCustomerData, useBoundStore.getState().getCustomerData());
    processFieldValidationErrors(enrichedCustomerData.fieldValidationErrors);
  }
  return {};
};
